import { Injectable } from '@angular/core';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { HttpClient } from '@angular/common/http';
import { DateService } from '../date/date.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { Observable } from 'rxjs/Observable';
import { DailyObservationEditor } from '../../models/daily-observations/daily-observations';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { cloneDeep } from 'lodash';
import { TimeZoneChangeEditor } from '../../models/time-zones/time-zone.models';
import { AppConfig } from '../../app/app.config';

@Injectable()
export class DailyObservationService {
  private readonly dailyObservationTable: DbTable;
  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService
  ) {
    this.dailyObservationTable = this.dbContext.tables.find(t => t.name === 'DailyObservationData');
  }

  // note that resulting editor.date has not been deserialised here, as all we need is the id
  async saveEditor(editor: DailyObservationEditor): Promise<number> {
    editor.date.dateTime = this.dateParserService.removeOffsetFromLuxonDate(editor.date.dateTime);
    const url = `${this.config.apiUrl}api/observations/save`;
    return this.http.post<number>(url, editor).toPromise();
  }

  deleteAllDailyObservationRecords() {
    return this.dbContext.removeAll(this.dailyObservationTable);
  }

  async saveDailyObservationChangeMultipleRecords(records: DailyObservationEditor[]) {
    records = this.dateParserService.removeOffsetFromLuxonDates(records);
    for (let i = 0; i < records.length; i++) {
      await this.dbContext.addOrUpdate(this.dailyObservationTable, records[i].id ? records[i].id : records[i].localId, records[i]);
    }
  }

  async getObservationDataForDay(date?: PtLuxonDate): Promise<DailyObservationEditor> {
    let instances = await this.dbContext.getAll(this.dailyObservationTable) as DailyObservationEditor[];

    instances = await this.applyOffsetsToObservationTimes(instances);
    const filteredInstances: DailyObservationEditor[] = [];

    for (let i = 0; i < instances.length; i++) {
      const sameDay = await this.dateService.compareDayOffsetsPreApplied(date.dateTime, instances[i].date.dateTime);
      if (sameDay) {
        filteredInstances.push(instances[i]);
      }
    }

    let observationEditor = filteredInstances[0];

    if (!!observationEditor) {
      return observationEditor;
    }

    observationEditor = new DailyObservationEditor();
    observationEditor.date = date;

    return observationEditor;
  }

  validate(editor: DailyObservationEditor): Observable<void> {
    editor.date.dateTime = this.dateParserService.removeOffsetFromLuxonDate(editor.date.dateTime);
    const url = `${this.config.apiUrl}api/observations/validate`;
    return this.http.post<void>(url, editor);
  }

  async applyOffsetsToObservationTimes(
    instances: DailyObservationEditor[],
  ): Promise<DailyObservationEditor[]> {
    for (let i = 0; i < instances.length; i++) {
      if (instances[i].date) {
        // remove system offset before getting timezone, or wrong timezone could be found
        instances[i].date.dateTime = await this.dateParserService.deserializeDate(instances[i].date.dateTime, true, this.timeZoneService);
      }
    }

    return instances;
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-observation/daily-observation.service.ts